/**
 * CountdownManager - Manages all countdown timers on the site
 */
class CountdownManager {
  constructor() {
    this.timers = new Map(); // Active timer instances
    this.intervals = new Set(); // Track all interval IDs
    this.storagePrefix = 'countdown_end_';
    this.initialized = false;
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!window._countdownManager) {
      window._countdownManager = new CountdownManager();
    }
    return window._countdownManager;
  }

  /**
   * Initialize all countdown timers on the page
   */
  init() {
    // Prevent double initialization
    if (this.initialized) {
      this.cleanup();
    }

    this.initialized = true;
    this.initializeTimers();
    this.attachEventListeners();
  }

  /**
   * Clean up all timers and intervals
   */
  cleanup() {
    // Clear all intervals
    this.intervals.forEach(intervalId => {
      clearInterval(intervalId);
    });
    this.intervals.clear();

    // Clear timer instances
    this.timers.clear();

    // Remove any observer flags from DOM
    document.querySelectorAll('[data-countdown-active]').forEach(el => {
      el.removeAttribute('data-countdown-active');
    });
  }

  /**
   * Initialize all countdown timers found on page
   */
  initializeTimers() {
    // Handle legacy countdown timers (backwards compatibility)
    this.initLegacyTimers();

    // Handle enhanced countdown timers
    this.initEnhancedTimers();
  }

  /**
   * Initialize legacy countdown timers for backwards compatibility
   */
  initLegacyTimers() {
    document.querySelectorAll('.countdown-timer:not([data-countdown-enhanced])').forEach(elem => {
      if (elem.hasAttribute('data-countdown-active')) return;
      elem.setAttribute('data-countdown-active', 'true');

      const endTime = Number(elem.dataset.time) * 1000;
      if (!endTime || endTime < Date.now()) {
        elem.style.display = 'none';
        return;
      }

      this.createLegacyTimer(elem, endTime);
    });
  }

  /**
   * Create a legacy format timer
   */
  createLegacyTimer(element, endTime) {
    const timerId = `legacy_${Date.now()}_${Math.random()}`;

    const updateTimer = () => {
      const now = Date.now();
      const difference = endTime - now;

      if (difference <= 0) {
        element.style.display = 'none';
        const intervalId = this.timers.get(timerId)?.intervalId;
        if (intervalId) {
          clearInterval(intervalId);
          this.intervals.delete(intervalId);
        }
        this.timers.delete(timerId);
        return;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      const hoursElem = element.querySelector('[data-hours]');
      const minutesElem = element.querySelector('[data-minutes]');
      const secondsElem = element.querySelector('[data-seconds]');

      if (hoursElem) {
        hoursElem.innerText = `${hours}${element.dataset.textH || 'h'}`;
        hoursElem.setAttribute('aria-label', `${hours} ${element.dataset.textHours || 'hours'}`);
        if (hours === 0) {
          hoursElem.style.display = 'none';
        } else {
          hoursElem.style.display = '';
        }
      }

      if (minutesElem) {
        minutesElem.innerText = `${minutes}${element.dataset.textM || 'm'}`;
        minutesElem.setAttribute('aria-label', `${minutes} ${element.dataset.textMinutes || 'minutes'}`);
      }

      if (secondsElem) {
        secondsElem.innerText = `${seconds}${element.dataset.textS || 's'}`;
        secondsElem.setAttribute('aria-label', `${seconds} ${element.dataset.textSeconds || 'seconds'}`);
      }

      element.removeAttribute('hidden');
    };

    // Initial update
    updateTimer();

    // Set up interval
    const intervalId = setInterval(updateTimer, 1000);
    this.intervals.add(intervalId);
    this.timers.set(timerId, { element, intervalId, endTime });
  }

  /**
   * Initialize enhanced countdown timers with advanced features
   */
  initEnhancedTimers() {
    document.querySelectorAll('[data-countdown-enhanced]').forEach(elem => {
      if (elem.hasAttribute('data-countdown-active')) return;
      elem.setAttribute('data-countdown-active', 'true');

      const config = this.parseTimerConfig(elem);
      if (config.error) {
        // Config error - timer won't display
        return;
      }

      this.createEnhancedTimer(elem, config);
    });
  }

  /**
   * Parse timer configuration from element data attributes
   */
  parseTimerConfig(element) {
    const mode = element.dataset.countdownMode || 'specific';
    const showDays = element.dataset.countdownShowDays === 'true';
    const hideOnExpire = element.dataset.countdownHideOnExpire === 'true';

    const labels = {
      days: element.dataset.countdownLabelDays || 'Days',
      hours: element.dataset.countdownLabelHours || 'Hrs',
      minutes: element.dataset.countdownLabelMinutes || 'Mins',
      seconds: element.dataset.countdownLabelSeconds || 'Secs'
    };

    let endTime;

    if (mode === 'specific') {
      // Specific date mode
      const dateStr = element.dataset.countdownDate;
      if (!dateStr) {
        return { error: 'No countdown date specified' };
      }

      endTime = this.parseDate(dateStr);
      if (!endTime || endTime < Date.now()) {
        return { error: 'Invalid or past date' };
      }
    } else {
      // Relative duration mode
      const duration = parseInt(element.dataset.countdownDuration) || 400;
      endTime = this.getOrSetStoredEndTime(duration, element.id || 'default');
    }

    return {
      mode,
      endTime,
      showDays,
      hideOnExpire,
      labels,
      elementId: element.id
    };
  }

  /**
   * Parse date string in format: YYYY-MM-DD HH:MM
   * All dates are interpreted as EST (America/New_York timezone)
   * This ensures all visitors see the same countdown regardless of their location
   */
  parseDate(dateStr) {
    try {
      // Format: YYYY-MM-DD HH:MM
      let match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
      if (match) {
        const [_, year, month, day, hour, minute] = match;

        // Create ISO string for the target time
        const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00`;

        // Convert EST time to UTC timestamp using Intl API
        // This handles EST/EDT (daylight saving) automatically
        const targetTimestamp = this.convertESTtoUTC(isoString);
        if (targetTimestamp) {
          return targetTimestamp;
        }
      }

      // Fallback: Try native Date parsing (will use local timezone - not ideal but better than nothing)
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.getTime();
      }

      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Convert a date string (assumed to be in EST) to UTC timestamp
   * Handles EST/EDT (daylight saving) automatically
   */
  convertESTtoUTC(isoString) {
    try {
      // Parse the ISO string to get components
      const match = isoString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (!match) return null;

      const [_, year, month, day, hour, minute, second] = match;

      // Determine if the target date is in EST (-5) or EDT (-4)
      // EDT is from 2nd Sunday of March to 1st Sunday of November
      const targetDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const isDST = this.isEasternDST(targetDate);
      const offset = isDST ? 4 : 5; // EDT = UTC-4, EST = UTC-5

      // Create UTC timestamp by adding the offset hours
      const utcDate = new Date(Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hour) + offset,
        parseInt(minute),
        parseInt(second)
      ));

      return utcDate.getTime();
    } catch (e) {
      return null;
    }
  }

  /**
   * Check if a date falls within Eastern Daylight Time (EDT)
   * EDT: 2nd Sunday of March 2:00 AM to 1st Sunday of November 2:00 AM
   */
  isEasternDST(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    // March (month 2): DST starts 2nd Sunday at 2 AM
    // November (month 10): DST ends 1st Sunday at 2 AM

    if (month < 2 || month > 10) return false; // Jan, Feb, Dec = EST
    if (month > 2 && month < 10) return true;  // Apr-Oct = EDT

    // March: Find 2nd Sunday
    if (month === 2) {
      const firstDay = new Date(year, 2, 1).getDay();
      const secondSunday = firstDay === 0 ? 8 : 15 - firstDay;
      return day >= secondSunday;
    }

    // November: Find 1st Sunday
    if (month === 10) {
      const firstDay = new Date(year, 10, 1).getDay();
      const firstSunday = firstDay === 0 ? 1 : 8 - firstDay;
      return day < firstSunday;
    }

    return false;
  }

  /**
   * Get stored end time from localStorage or create new one
   */
  getOrSetStoredEndTime(durationMinutes, identifier) {
    const storageKey = `${this.storagePrefix}${identifier}_${durationMinutes}`;

    try {
      let storedEnd = localStorage.getItem(storageKey);

      if (storedEnd) {
        storedEnd = parseInt(storedEnd);
        // Check if still valid (not expired)
        if (storedEnd > Date.now()) {
          return storedEnd;
        }
      }

      // Create new end time
      const newEndTime = Date.now() + (durationMinutes * 60 * 1000);
      localStorage.setItem(storageKey, newEndTime.toString());
      return newEndTime;

    } catch (e) {
      // LocalStorage might be full or disabled
      // Fallback to session-only timer
      return Date.now() + (durationMinutes * 60 * 1000);
    }
  }

  /**
   * Create an enhanced timer instance
   */
  createEnhancedTimer(element, config) {
    const timerId = config.elementId || `timer_${Date.now()}_${Math.random()}`;

    const updateTimer = () => {
      const now = Date.now();
      const difference = config.endTime - now;

      if (difference <= 0) {
        this.handleTimerExpiry(element, timerId, config);
        return;
      }

      const time = this.calculateTimeComponents(difference);
      this.updateTimerDisplay(element, time, config);
    };

    // Initial update
    updateTimer();
    element.removeAttribute('hidden');

    // Set up interval
    const intervalId = setInterval(updateTimer, 1000);
    this.intervals.add(intervalId);
    this.timers.set(timerId, {
      element,
      intervalId,
      config
    });
  }

  /**
   * Calculate time components from milliseconds
   */
  calculateTimeComponents(milliseconds) {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    return {
      days: days,
      hours: hours % 24,
      minutes: minutes % 60,
      seconds: seconds % 60
    };
  }

  /**
   * Update timer display elements
   */
  updateTimerDisplay(element, time, config) {
    const format = (n) => n.toString().padStart(2, '0');

    // Handle days display based on config
    const daysBlock = element.querySelector('[data-days-block]');
    const daysValue = element.querySelector('[data-days-value]');
    const daysLabel = element.querySelector('[data-days-label]');
    const daysSeparator = daysBlock ? daysBlock.nextElementSibling : null;

    if (config.showDays && daysBlock) {
      // Show days if configured
      if (daysValue) {
        daysValue.textContent = format(time.days);
      }
      if (daysLabel) {
        daysLabel.textContent = time.days === 1 ? 'Day' : config.labels.days;
      }
      // Hide days block only when days is 0
      if (time.days === 0) {
        daysBlock.style.display = 'none';
        if (daysSeparator && daysSeparator.classList.contains('countdown-separator')) {
          daysSeparator.style.display = 'none';
        }
      } else {
        daysBlock.style.display = '';
        if (daysSeparator && daysSeparator.classList.contains('countdown-separator')) {
          daysSeparator.style.display = '';
        }
      }
    } else if (daysBlock) {
      // Hide days if not configured
      daysBlock.style.display = 'none';
      if (daysSeparator && daysSeparator.classList.contains('countdown-separator')) {
        daysSeparator.style.display = 'none';
      }
    }

    // Update hours
    const hoursValue = element.querySelector('[data-hours-value]');
    if (hoursValue) {
      hoursValue.textContent = format(time.hours);
    }

    // Update minutes
    const minutesValue = element.querySelector('[data-minutes-value]');
    if (minutesValue) {
      minutesValue.textContent = format(time.minutes);
    }

    // Update seconds
    const secondsValue = element.querySelector('[data-seconds-value]');
    if (secondsValue) {
      secondsValue.textContent = format(time.seconds);
    }
  }

  /**
   * Handle timer expiry
   */
  handleTimerExpiry(element, timerId, config) {
    // Clear interval
    const timer = this.timers.get(timerId);
    if (timer && timer.intervalId) {
      clearInterval(timer.intervalId);
      this.intervals.delete(timer.intervalId);
    }
    this.timers.delete(timerId);

    // Clear localStorage if it was a relative timer
    if (config.mode === 'relative' && config.elementId) {
      try {
        const duration = parseInt(element.dataset.countdownDuration) || 400;
        const storageKey = `${this.storagePrefix}${config.elementId}_${duration}`;
        localStorage.removeItem(storageKey);
      } catch (e) {
        // Ignore localStorage errors
      }
    }

    // Hide or show zero
    if (config.hideOnExpire) {
      element.style.display = 'none';

      // If in carousel, might need to advance to next slide
      const carousel = element.closest('.carousel-item');
      if (carousel) {
        this.handleCarouselTimerExpiry(carousel);
      }
    } else {
      // Show 00:00:00:00
      const zeroTime = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      this.updateTimerDisplay(element, zeroTime, config);
    }

    // Dispatch custom event
    const event = new CustomEvent('countdown:expired', {
      detail: {
        element: element,
        elementId: config.elementId,
        mode: config.mode
      },
      bubbles: true
    });
    element.dispatchEvent(event);
  }

  /**
   * Handle timer expiry in carousel context
   */
  handleCarouselTimerExpiry(carouselItem) {
    // Find the carousel container
    const carousel = carouselItem.closest('.carousel');
    if (!carousel) return;

    // Check if this is the active item
    if (!carouselItem.classList.contains('active')) return;

    // Check if there are other items
    const allItems = carousel.querySelectorAll('.carousel-item');
    if (allItems.length <= 1) return;

    // Trigger next slide
    const carouselInstance = bootstrap.Carousel.getOrCreateInstance(carousel);
    if (carouselInstance) {
      carouselInstance.next();
    }
  }

  /**
   * Attach global event listeners
   */
  attachEventListeners() {
    // Handle visibility change to pause/resume timers
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Page is hidden, pause timers to save CPU
        this.pauseAll();
      } else {
        // Page is visible, resume timers
        this.resumeAll();
      }
    });

    // Handle carousel slide events
    document.addEventListener('slide.bs.carousel', (event) => {
      this.handleCarouselSlide(event);
    });

    // Clean up on page unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });
  }

  /**
   * Pause all active timers
   */
  pauseAll() {
    this.intervals.forEach(intervalId => {
      clearInterval(intervalId);
    });
    this.intervals.clear();
  }

  /**
   * Resume all paused timers
   */
  resumeAll() {
    this.timers.forEach((timer, timerId) => {
      if (!timer.intervalId || !this.intervals.has(timer.intervalId)) {
        // Recreate the timer
        if (timer.config) {
          this.createEnhancedTimer(timer.element, timer.config);
        }
      }
    });
  }

  /**
   * Handle carousel slide events
   */
  handleCarouselSlide(event) {
    const { from, to, relatedTarget } = event;

    // Initialize timer in new slide if needed
    const newSlide = relatedTarget || event.target.querySelectorAll('.carousel-item')[to];
    if (newSlide) {
      const timers = newSlide.querySelectorAll('[data-countdown-enhanced]:not([data-countdown-active])');
      timers.forEach(timer => {
        const config = this.parseTimerConfig(timer);
        if (!config.error) {
          timer.setAttribute('data-countdown-active', 'true');
          this.createEnhancedTimer(timer, config);
        }
      });
    }
  }
}

function initCountdownTimers() {
  CountdownManager.getInstance().init();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCountdownTimers);
} else {
  initCountdownTimers();
}

// Re-initialize on Shopify section load (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.countdown-timer') ||
      event.target.querySelector('[data-countdown-enhanced]')) {
    initCountdownTimers();
  }
});

// Re-initialize on Shopify section reorder
document.addEventListener('shopify:section:reorder', () => {
  initCountdownTimers();
});

// Export for debugging
window.CountdownManager = CountdownManager;

/**
 * CartEnhancementManager - Manages cart drawer enhancements
 */
class CartEnhancementManager {
  constructor() {
    this.initialized = false;
    this.cartObserver = null;
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!window._cartEnhancementManager) {
      window._cartEnhancementManager = new CartEnhancementManager();
    }
    return window._cartEnhancementManager;
  }

  /**
   * Initialize cart enhancements
   */
  async init() {
    if (this.initialized) return;
    this.initialized = true;

    // CRITICAL FIX: Fetch cart data on page load BEFORE any calculations
    await this.fetchAndUpdateCartData();

    this.initVariantToggle();
    this.initCurrencyConversion();
    this.observeCartChanges();
    this.attachEventListeners();
  }

  /**
   * Initialize variant show/hide toggle functionality
   * Adapted for Ecomify theme structure
   */
  initVariantToggle() {
    // Target each cart product item
    const cartItems = document.querySelectorAll('.cart-product-item');

    cartItems.forEach(item => {
      const variantContainer = item.querySelector('.cart-item-variants');
      if (!variantContainer) return;

      // Get direct children elements to hide/show
      // 1. variant title <small> (immediate child) - ALWAYS HIDE
      // 2. selling plan <small> (immediate child)
      // 3. property <li> elements (inside <ul>)
      const variantTitle = variantContainer.querySelector('.variant-title');
      const otherSmallElements = Array.from(variantContainer.querySelectorAll(':scope > small')).filter(el => !el.classList.contains('variant-title'));
      const propertyList = variantContainer.querySelector('.list-unstyled');
      const propertyItems = propertyList ? Array.from(propertyList.querySelectorAll('li')) : [];

      // Combine other elements (not variant title)
      const otherElements = [...otherSmallElements, ...propertyItems];

      // Track hidden elements
      const hiddenElements = [];

      // Always hide variant title
      if (variantTitle) {
        variantTitle.classList.add('cart-variant-hidden');
        hiddenElements.push(variantTitle);
      }

      // Hide other elements beyond the 2nd (keep first 2 visible)
      otherElements.forEach((elem, index) => {
        if (index > 1) {  // Changed from >= 2 to > 1 for clarity
          elem.classList.add('cart-variant-hidden');
          hiddenElements.push(elem);
        }
      });

      // Only add toggle if we have hidden elements
      if (hiddenElements.length === 0) return;

      // Check if toggle already exists
      if (variantContainer.querySelector('.cart-variant-toggle')) return;

      // Create toggle button
      const toggleBtn = document.createElement('button');
      toggleBtn.className = 'cart-variant-toggle';
      toggleBtn.textContent = 'Show more';
      toggleBtn.setAttribute('type', 'button');
      toggleBtn.style.cssText = `
        color: #666;
        font-size: 11px;
        font-weight: 400;
        text-decoration: underline;
        cursor: pointer;
        padding: 0;
        border: none;
        background: none;
        margin-top: 2px;
        display: block;
      `;

      // Fixed toggle logic with proper expand/collapse
      toggleBtn.onclick = () => {
        const isExpanded = toggleBtn.classList.contains('expanded');

        if (isExpanded) {
          // Collapse - hide the elements that should be hidden
          hiddenElements.forEach(elem => {
            elem.classList.add('cart-variant-hidden');
          });
          toggleBtn.classList.remove('expanded');
          toggleBtn.textContent = 'Show more';
        } else {
          // Expand - show all hidden elements
          hiddenElements.forEach(elem => {
            elem.classList.remove('cart-variant-hidden');
          });
          toggleBtn.classList.add('expanded');
          toggleBtn.textContent = 'Show less';
        }
      };

      variantContainer.appendChild(toggleBtn);
    });
  }

  /**
   * Initialize currency conversion for free shipping bar
   */
  initCurrencyConversion() {
    this.updateShippingThreshold();
  }

  /**
   * Update shipping threshold based on cart total and settings
   */
  updateShippingThreshold(cartData = null) {
    // Get configuration from data attributes
    const config = document.getElementById('cart-shipping-config');
    if (!config) return;

    const thresholdCents = parseInt(config.dataset.thresholdCents) || 6000; // Default $60
    const shippingCostCents = parseInt(config.dataset.shippingCost) || 695; // Default to $6.95
    const currencyCode = config.dataset.thresholdCurrency || 'USD';

    // Get cart total in cents (use passed data, window data, or DOM)
    let cartTotalCents = 0;
    const cart = cartData || window.cartData;

    if (cart && cart.total_price !== undefined) {
      cartTotalCents = cart.total_price;
    } else {
      // Fallback: parse from displayed subtotal with correct selector
      const subtotalElement = document.querySelector('[data-subtotal]');
      if (subtotalElement) {
        // Better parsing to handle currency formatting
        const subtotalText = subtotalElement.textContent.trim();
        const subtotalMatch = subtotalText.match(/[\d,]+\.?\d*/);
        if (subtotalMatch) {
          cartTotalCents = Math.round(parseFloat(subtotalMatch[0].replace(',', '')) * 100);
        }
      }
    }

    // Update ALL shipping text elements (handle multiple locations)
    const shippingTexts = document.querySelectorAll('[data-cart-shipping-calculator]');
    shippingTexts.forEach(shippingText => {
      if (cartTotalCents >= thresholdCents) {
        // Only update if not already showing FREE
        if (!shippingText.innerHTML.includes('FREE')) {
          shippingText.innerHTML = '<strong>FREE</strong>';
        }
        shippingText.classList.add('text-primary');
      } else {
        // Format shipping cost
        const shippingFormatted = (shippingCostCents / 100).toFixed(2);
        // Only update if different
        if (!shippingText.innerHTML.includes(shippingFormatted)) {
          shippingText.innerHTML = `$${shippingFormatted}`;
        }
        shippingText.classList.remove('text-primary');
      }
    });

    // Also update progress bar if exists (for free shipping goal)
    const progressBar = document.querySelector('#progress-bar');
    const messageElement = document.querySelector('#shipping-message');

    if (progressBar && messageElement) {
      if (cartTotalCents >= thresholdCents) {
        messageElement.textContent = "You've unlocked FREE Shipping!";
        progressBar.style.width = '100%';
        progressBar.classList.add('free-shipping-unlocked');
      } else {
        const remainingCents = thresholdCents - cartTotalCents;
        const progress = Math.min((cartTotalCents / thresholdCents) * 100, 100);

        const formatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });

        const remainingDollars = remainingCents / 100;
        messageElement.textContent = `Unlock FREE Shipping with ${formatter.format(remainingDollars)} More!`;
        progressBar.style.width = `${progress}%`;
        progressBar.classList.remove('free-shipping-unlocked');
      }
    }
  }

  /**
   * Fetch and update cart data from Shopify API
   */
  async fetchAndUpdateCartData() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      window.cartData = cartData; // Store globally for other functions
      return cartData;
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
      return null;
    }
  }

  /**
   * Get current cart total
   */
  getCartTotal() {
    // First try to get from window.cartData
    if (window.cartData && window.cartData.total_price !== undefined) {
      return window.cartData.total_price / 100; // Convert cents to dollars
    }

    // Fallback to DOM parsing with correct selector
    const subtotalElement = document.querySelector('[data-subtotal]');
    if (!subtotalElement) return 0;

    // Extract numeric value from money string
    const moneyString = subtotalElement.textContent;
    const numericValue = parseFloat(moneyString.replace(/[^0-9.-]+/g, ''));

    // Convert cents to dollars if needed
    return numericValue > 1000 ? numericValue / 100 : numericValue;
  }

  /**
   * Format money based on currency
   */
  formatMoney(amount) {
    const currencyCode = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      // Fallback to basic formatting
      return `${currencyCode} ${amount.toFixed(2)}`;
    }
  }

  /**
   * Observe cart DOM changes and re-initialize features
   */
  observeCartChanges() {
    if (this.cartObserver) {
      this.cartObserver.disconnect();
    }

    const cartBody = document.querySelector('#offcanvas-cart .offcanvas-body');
    if (!cartBody) {
      // Retry if cart not loaded yet
      setTimeout(() => this.observeCartChanges(), 500);
      return;
    }

    this.cartObserver = new MutationObserver((mutations) => {
      let shouldReinit = false;

      mutations.forEach(mutation => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1 &&
                (node.classList?.contains('cart-product-item') ||
                 node.querySelector?.('.cart-product-item'))) {
              shouldReinit = true;
            }
          });
        }
      });

      if (shouldReinit) {
        // Debounce re-initialization
        clearTimeout(this.reinitTimeout);
        this.reinitTimeout = setTimeout(() => {
          this.initVariantToggle();
          this.updateShippingThreshold();
        }, 200);
      }
    });

    this.cartObserver.observe(cartBody, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Attach event listeners for cart updates
   */
  attachEventListeners() {
    // Listen for cart updates
    window.addEventListener('updated.ecomify.cart', async (e) => {
      // Step 1: Wait for DOM to stabilize
      await new Promise(resolve => setTimeout(resolve, 100));

      // Step 2: Use cart data from event or fetch if not provided
      const cartData = e.detail?.cart || await this.fetchAndUpdateCartData();

      // Step 3: Update UI elements with cart data
      if (cartData) {
        this.updateShippingThreshold(cartData);
      }

      // Step 4: Reinitialize variant toggles
      this.initVariantToggle();
    });

    // Also listen for cart:updated event (old theme compatibility)
    document.addEventListener('cart:updated', async () => {
      // Same logic as above
      await new Promise(resolve => setTimeout(resolve, 200));
      const cartData = await this.fetchAndUpdateCartData();
      if (cartData) {
        this.updateShippingThreshold(cartData);
      }
      this.initVariantToggle();
    });

    // Re-initialize when cart drawer opens
    const cartOffcanvas = document.getElementById('offcanvas-cart');
    if (cartOffcanvas) {
      cartOffcanvas.addEventListener('shown.bs.offcanvas', () => {
        this.initVariantToggle();
        this.updateShippingThreshold();

        // Check if BOGO modal should be shown
        if (typeof BOGOUtilsAdapter !== 'undefined') {
          const bogoAdapter = BOGOUtilsAdapter.getInstance();
          // Fetch fresh cart data and update step
          bogoAdapter.fetchCartData().then(() => {
            bogoAdapter.updateProgressBar();
            // Check if we're at Step 2 (not just count === 1)
            if (bogoAdapter.currentStep === 2) {
              bogoAdapter.checkAndShowBOGOModal();
            }
          });
        }
      });
    }

    // Listen for currency changes
    document.addEventListener('currency:changed', () => {
      this.updateShippingThreshold();
    });
  }

  /**
   * Cleanup
   */
  cleanup() {
    if (this.cartObserver) {
      this.cartObserver.disconnect();
      this.cartObserver = null;
    }
    clearTimeout(this.updateTimeout);
    this.initialized = false;
  }
}

// Initialize cart enhancements
async function initCartEnhancements() {
  // CRITICAL: Await async init to ensure cart data is fetched on page load
  const manager = CartEnhancementManager.getInstance();
  await manager.init();

  // Expose to window for cart.js to access
  window.CartEnhancementManager = manager;

  // Make entire gift box clickable for better mobile UX
  initGiftBoxClickHandlers();
}

// Make gift box cards fully clickable
function initGiftBoxClickHandlers() {
  // Remove any existing listeners to prevent duplicates
  if (window.giftBoxClickHandler) {
    document.removeEventListener('click', window.giftBoxClickHandler);
  }

  // Create the click handler
  window.giftBoxClickHandler = function(e) {
    // Check if clicked element is within a gift box item
    const giftBox = e.target.closest('.gift-box-item');
    if (giftBox) {
      // Don't trigger if clicking directly on the add button
      if (e.target.closest('.gift-box-add')) return;

      // Find and click the add button within this gift box
      const addButton = giftBox.querySelector('.gift-box-add');
      if (addButton && !addButton.disabled) {
        // Provide visual feedback
        giftBox.style.opacity = '0.7';
        setTimeout(() => {
          giftBox.style.opacity = '';
        }, 150);

        // Click the button
        addButton.click();
      }
      return;
    }

    // Check if clicked element is within a warranty card
    const warrantyCard = e.target.closest('.warranty-card');
    if (warrantyCard) {
      // Don't trigger if clicking directly on the add button
      if (e.target.closest('.warranty-add')) return;

      // Find and click the add button within this warranty card
      const addButton = warrantyCard.querySelector('.warranty-add');
      if (addButton && !addButton.disabled) {
        // Provide visual feedback
        warrantyCard.style.opacity = '0.7';
        setTimeout(() => {
          warrantyCard.style.opacity = '';
        }, 150);

        // Click the button
        addButton.click();
      }
    }
  };

  // Add the event listener
  document.addEventListener('click', window.giftBoxClickHandler);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartEnhancements);
} else {
  initCartEnhancements();
}

// Re-initialize on Shopify events
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('#offcanvas-cart')) {
    initCartEnhancements();
  }
});

/* ==================================
   Upsell System
   ================================== */

// Upsell variant IDs to exclude from eligible item counting
// Update this array if you change upsell products in theme settings
const UPSELL_VARIANT_IDS = [
  34054986596483,  // Warranty/Protection
  34323290521731,  // Gift Box
  43449192841347,  // Engraving
  43636224491651,  // Gift Message
  44421842534531   // Additional Upsell
];

// Shared function to count eligible items (excluding upsells)
// Used by cart goals and upsell quantity suggestions
window.getEligibleItemCount = async function() {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();

    let count = 0;
    cart.items.forEach(item => {
      if (!UPSELL_VARIANT_IDS.includes(item.variant_id)) {
        count += item.quantity;
      }
    });

    return count;
  } catch (error) {
    console.error('Error fetching cart for eligible count:', error);
    return 1; // Default to 1 on error
  }
};

// Helper function for adding upsell products
// Supports dynamic quantity via data-quantity attribute
window.addToCartUpsell = function(button) {
  const variantId = button.dataset.variantId;
  if (!variantId) return;

  // Support dynamic quantity (default to 1)
  const quantity = parseInt(button.dataset.quantity) || 1;

  // Store original text for reset
  const originalText = button.textContent;

  // Add loading state
  button.disabled = true;
  button.textContent = 'Adding...';

  // Add to cart via AJAX
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      id: variantId,
      quantity: quantity
    })
  })
  .then(response => {
    // Check if the request was successful
    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    // Add loading class for visual feedback
    button.classList.add('btn--loading');

    // Pass the Response object to refreshCartContents (NOT parsed JSON)
    if (window.refreshCartContents) {
      window.refreshCartContents(response);
    }

    // Reset button after a short delay
    setTimeout(() => {
      button.classList.remove('btn--loading');
      button.textContent = originalText;
      button.disabled = false;
    }, 500);
  })
  .catch(error => {
    console.error('Error adding to cart:', error);
    // Error adding to cart
    button.classList.remove('btn--loading');
    button.textContent = originalText;
    button.disabled = false;
  });
};

/**
 * UpsellQuantityManager
 * Updates gift box button text based on eligible item count
 * Shows "Add 2" when customer has 2+ items in cart
 */
class UpsellQuantityManager {
  static async updateButtons() {
    const count = await window.getEligibleItemCount();
    const giftBoxButtons = document.querySelectorAll('.gift-box-add');

    giftBoxButtons.forEach(button => {
      // Don't update if button is in loading state
      if (button.disabled || button.classList.contains('btn--loading')) return;

      if (count > 1) {
        button.textContent = `+ Add ${count}`;
        button.dataset.quantity = count;
      } else {
        button.textContent = '+ Add';
        button.dataset.quantity = 1;
      }
    });
  }

  static init() {
    // Initial update
    UpsellQuantityManager.updateButtons();

    // Update on cart changes
    window.addEventListener('updated.ecomify.cart', () => {
      // Small delay to ensure cart data is fresh
      setTimeout(() => {
        UpsellQuantityManager.updateButtons();
      }, 100);
    });
  }
}

// Initialize UpsellQuantityManager
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    UpsellQuantityManager.init();
  });
} else {
  UpsellQuantityManager.init();
}

/**
 * BOGO Progress Bar Manager - FIXED VERSION
 * UI-only manager, business logic handled by BOGOUtilsAdapter
 * Fixed: Removed broken cart fetching and step calculation
 */
class BOGOProgressManager {
  constructor() {
    this.currentStep = 1;
    this.totalSteps = 3;
    this.progressBars = [];
    this.init();
  }

  static getInstance() {
    if (!window._bogoProgressManager) {
      window._bogoProgressManager = new BOGOProgressManager();
    }
    return window._bogoProgressManager;
  }

  init() {
    // Initialize all BOGO progress bars on the page
    this.progressBars = document.querySelectorAll('[data-bogo-progress-bar]');

    if (this.progressBars.length === 0) return;

    // Read initial state from first progress bar's data attributes
    const firstBar = this.progressBars[0];
    const initialStep = parseInt(firstBar.dataset.initialStep || '1', 10);
    const eligibleCount = parseInt(firstBar.dataset.eligibleCount || '0', 10);

    // Set initial step from server-side calculation
    if (initialStep && initialStep >= 1 && initialStep <= 3) {
      this.currentStep = initialStep;
      // The HTML already has correct classes, just update our state
    }

    // Listen for theme editor section reload
    document.addEventListener('shopify:section:load', (e) => {
      if (e.target.querySelector('[data-bogo-progress-bar]')) {
        this.reinit();
      }
    });

    // BOGOUtilsAdapter will control all step updates
  }

  reinit() {
    // Clean up and reinitialize for theme editor
    this.progressBars = document.querySelectorAll('[data-bogo-progress-bar]');
    // Step will be set by BOGOUtilsAdapter
  }

  setStep(step) {
    if (step < 1 || step > this.totalSteps) return;

    this.currentStep = step;

    // Re-query in case DOM changed
    this.progressBars = document.querySelectorAll('[data-bogo-progress-bar]');

    this.progressBars.forEach(progressBar => {
      // Update step buttons
      const stepButtons = progressBar.querySelectorAll('.bogo-progress-step');
      stepButtons.forEach((button, index) => {
        const buttonStep = index + 1;

        // Remove all states first
        button.classList.remove('bogo-step--active', 'bogo-step--completed');

        if (buttonStep < step) {
          // Completed steps
          button.classList.add('bogo-step--completed');
        } else if (buttonStep === step) {
          // Current step
          button.classList.add('bogo-step--active');
        }
        // Steps after current remain default (inactive)
      });

      // Update progress line
      const progressLine = progressBar.querySelector('.bogo-progress-line-fill');
      if (progressLine) {
        // Calculate percentage (0%, 50%, 100% for 3 steps)
        const percentage = ((step - 1) / (this.totalSteps - 1)) * 100;
        progressLine.style.width = `${percentage}%`;
      }
    });

    // Dispatch event for other components
    document.dispatchEvent(new CustomEvent('bogo:step-changed', {
      detail: { step: step }
    }));
  }

  // Public method to get current step
  getStep() {
    return this.currentStep;
  }

  reset() {
    this.setStep(1);
  }
}

/**
 * BOGOUtilsAdapter - Complete BOGO functionality for Ecomify theme
 * Handles button updates, state management, and modal triggers
 * Fixed version with proper selectors and observers
 */
class BOGOUtilsAdapter {
  constructor() {
    this.progressManager = BOGOProgressManager.getInstance();
    this.excludedKeywords = ['warranty', 'gift-box', 'gift box', 'engraving', 'protection'];
    this.bogoDiscountTitle = null;
    this.eligibleItemCount = 0;
    this.currentStep = 1; // Track the current BOGO step
    this.modalShown = false;
    this.cartData = null;

    // Debounce timers
    this.updateDebounceTimer = null;
    this.cartFetchTimer = null;

    // Observers
    this.buttonTextObservers = new Map();
    this.buttonDisabledObserver = null;
    this.pendingButtonUpdates = new Map();

    this.init();
  }

  static getInstance() {
    if (!window._bogoUtilsAdapter) {
      window._bogoUtilsAdapter = new BOGOUtilsAdapter();
    }
    return window._bogoUtilsAdapter;
  }

  init() {
    // Listen to cart updates (new theme events)
    window.addEventListener('updated.ecomify.cart', () => this.handleCartUpdate());

    // Listen for variant changes on product pages
    document.addEventListener('variant:change', () => this.debouncedButtonUpdate());

    // Watch for Shopify theme editor events
    document.addEventListener('shopify:section:load', () => this.reinit());
    document.addEventListener('shopify:section:unload', () => this.cleanup());

    // Handle inline free item selector add-to-cart clicks
    // FIXED: Now properly scoped to modal only
    this.initializeInlineAddToCart();

    // Initialize on page load - CRITICAL FIX
    this.initializeOnPageLoad();

    // Check sessionStorage for persisted state
    this.loadPersistedState();
  }

  /**
   * Initialize cart data and UI on page load
   * SIMPLIFIED: Immediately fetch cart and update
   */
  async initializeOnPageLoad() {
    try {
      // Immediately fetch cart data without delays
      await this.fetchCartData();

      // Update UI based on fetched cart data
      await this.handleCartUpdate();

      // Set up observers after cart data is ready
      this.observeButtons();
      this.observeButtonDisabledStates();
    } catch (error) {
      // Try to proceed with empty cart assumption
      this.cartData = { items: [], total_price: 0 };
      this.updateProgressBar();
    }
  }

  /**
   * Fetch cart data - simplified to always get fresh data
   */
  async fetchCartData() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const data = await response.json();
      this.cartData = data;
      window.cartData = data; // Share with other scripts

      return data;

    } catch (error) {
      // Return empty cart on error
      this.cartData = { items: [], total_price: 0 };
      return this.cartData;
    }
  }

  /**
   * Debounced button update to prevent rapid changes
   */
  debouncedButtonUpdate() {
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }

    this.updateDebounceTimer = setTimeout(() => {
      this.fetchCartData().then(() => {
        this.updateProductButtons();
      });
    }, 200);
  }

  /**
   * Check if an item should be excluded from BOGO
   */
  isExcludedItem(item) {
    if (!item) return true;

    const title = (item.product_title || item.title || '').toLowerCase();
    const variantTitle = (item.variant_title || '').toLowerCase();
    const properties = item.properties || {};

    // Check title and variant for excluded keywords
    for (const keyword of this.excludedKeywords) {
      if (title.includes(keyword) || variantTitle.includes(keyword)) {
        return true;
      }
    }

    // Check properties for exclusions
    for (const [key, value] of Object.entries(properties)) {
      const propKey = key.toLowerCase();
      const propValue = (value || '').toString().toLowerCase();

      if (propKey.includes('warranty') || propKey.includes('protection') ||
          propValue.includes('warranty') || propValue.includes('protection')) {
        return true;
      }
    }

    // Check for BOGO item flag
    if (properties._bogo_item === 'true') {
      return true; // Don't apply BOGO to already free items
    }

    return false;
  }

  /**
   * Get count of eligible items for BOGO
   */
  getEligibleItemCount() {
    if (!this.cartData || !this.cartData.items) return 0;

    let count = 0;
    for (const item of this.cartData.items) {
      if (!this.isExcludedItem(item)) {
        // Count quantity for proper step calculation
        count += item.quantity;
      }
    }

    return count;
  }

  /**
   * Check if cart has BOGO discount applied
   */
  hasBOGODiscount() {
    if (!this.cartData) return false;

    // Check cart-level discounts
    if (this.cartData.cart_level_discount_applications) {
      for (const discount of this.cartData.cart_level_discount_applications) {
        const title = (discount.title || '').toLowerCase();
        if (title.includes('bogo') || title.includes('buy one') || title.includes('free')) {
          this.bogoDiscountTitle = discount.title;
          return true;
        }
      }
    }

    // Check line-level discounts
    if (this.cartData.items) {
      for (const item of this.cartData.items) {
        if (item.line_level_discount_allocations) {
          for (const discount of item.line_level_discount_allocations) {
            const title = (discount.discount_application?.title || '').toLowerCase();
            if (title.includes('bogo') || title.includes('buy one') || title.includes('free')) {
              this.bogoDiscountTitle = discount.discount_application?.title;
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * Update product page buttons based on BOGO eligibility
   * FIXED: Shows "ADD ITEM FOR FREE" when customer has exactly 1 eligible item
   */
  updateProductButtons() {
    const eligibleCount = this.getEligibleItemCount();

    // Find all add to cart buttons - FIXED SELECTORS
    const buttons = document.querySelectorAll(
      '.btn-atc, button[name="add"], .product-form__submit, .btn-add-to-cart'
    );

    buttons.forEach(button => {
      try {
        // Skip if button is in cart or modal
        if (button.closest('.offcanvas-cart') || button.closest('#bogo-recommendation-modal')) {
          return;
        }

        // Check if button is disabled (for personalized products)
        if (button.disabled) {
          // Queue update for when button is enabled
          if (eligibleCount === 1) {
            this.pendingButtonUpdates.set(button, true);
          } else {
            this.pendingButtonUpdates.delete(button);
          }
          return;
        }

        // Skip if button is for excluded products
        const form = button.closest('form');
        const productTitle = form?.querySelector('[data-product-title], .product-title, h1, h2')?.textContent || '';

        if (this.isExcludedByTitle(productTitle)) {
          this.resetButton(button);
          return;
        }

        // FIXED LOGIC: Show FREE button when customer has exactly 1 eligible item
        if (eligibleCount === 1) {
          // Customer has 1 item, next item will be free
          this.updateButtonForFree(button);
          this.observeButtonTextChanges(button);
        } else {
          this.resetButton(button);
          this.unobserveButtonTextChanges(button);
        }
      } catch (error) {
        // Error updating button - continue
      }
    });

    // Save state
    this.persistState();
  }

  /**
   * Check if product title indicates exclusion
   */
  isExcludedByTitle(title) {
    const lowerTitle = title.toLowerCase();
    for (const keyword of this.excludedKeywords) {
      if (lowerTitle.includes(keyword)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Update button to show BOGO offer
   * FIXED: Now targets correct elements in Ecomify theme
   */
  updateButtonForFree(button) {
    if (!button) return;

    // Store original text if not already stored
    const textElement = button.querySelector('.btn-atc-text, .btn-text, span') || button;

    if (!button.dataset.originalText) {
      button.dataset.originalText = textElement.textContent.trim();
    }

    // Update button text
    textElement.innerHTML = '🎁 ADD ITEM FOR FREE';
    button.classList.add('bogo-available');

    // Add pulse animation
    button.style.animation = 'bogo-pulse 2s infinite';

    // Mark as BOGO button
    button.dataset.bogoActive = 'true';
  }

  /**
   * Reset button to original state
   */
  resetButton(button) {
    if (!button) return;

    if (button.dataset.originalText) {
      const textElement = button.querySelector('.btn-atc-text, .btn-text, span') || button;
      textElement.textContent = button.dataset.originalText;
      delete button.dataset.originalText;
    }

    button.classList.remove('bogo-available');
    button.style.animation = '';
    delete button.dataset.bogoActive;
  }

  /**
   * Observe button text changes to maintain BOGO text
   * FIXED: Handles innerHTML replacement by theme
   */
  observeButtonTextChanges(button) {
    if (!button || this.buttonTextObservers.has(button)) return;

    const observer = new MutationObserver((mutations) => {
      // Check if button should show BOGO text
      if (button.dataset.bogoActive === 'true') {
        // Re-query for text element as it may have been replaced
        const textElement = button.querySelector('.btn-atc-text, .btn-text, span') || button;

        // Check if text was changed away from BOGO
        if (!textElement.innerHTML.includes('🎁')) {
          // Restore BOGO text with slight delay to ensure it sticks
          setTimeout(() => {
            const currentTextElement = button.querySelector('.btn-atc-text, .btn-text, span') || button;
            currentTextElement.innerHTML = '🎁 ADD ITEM FOR FREE';
          }, 10);
        }
      }
    });

    // Observe the button itself for any changes
    observer.observe(button, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['innerHTML']
    });

    this.buttonTextObservers.set(button, observer);
  }

  /**
   * Stop observing button text changes
   */
  unobserveButtonTextChanges(button) {
    const observer = this.buttonTextObservers.get(button);
    if (observer) {
      observer.disconnect();
      this.buttonTextObservers.delete(button);
    }
  }

  /**
   * Watch for button disabled state changes
   * NEW: Handles personalized products that start disabled
   */
  observeButtonDisabledStates() {
    if (this.buttonDisabledObserver) return;

    this.buttonDisabledObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          const button = mutation.target;

          // Check if button just became enabled and has pending update
          if (!button.disabled && this.pendingButtonUpdates.has(button)) {
            const shouldUpdate = this.pendingButtonUpdates.get(button);
            this.pendingButtonUpdates.delete(button);

            if (shouldUpdate) {
              setTimeout(() => this.updateButtonForFree(button), 50);
            }
          }
        }
      });
    });

    // Observe all buttons for disabled attribute changes
    const buttons = document.querySelectorAll('.btn-atc, button[name="add"]');
    buttons.forEach(button => {
      this.buttonDisabledObserver.observe(button, {
        attributes: true,
        attributeFilter: ['disabled']
      });
    });
  }

  /**
   * Watch for dynamic button rendering
   * ENHANCED: More robust observation
   */
  observeButtons() {
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach(mutation => {
        // Check if buttons were added
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.matches && (node.matches('.btn-atc, button[name="add"]') ||
                node.querySelector('.btn-atc, button[name="add"]'))) {
              shouldUpdate = true;

              // Also observe new buttons for disabled state
              const newButtons = node.matches('.btn-atc, button[name="add"]') ?
                [node] : node.querySelectorAll('.btn-atc, button[name="add"]');

              newButtons.forEach(btn => {
                if (this.buttonDisabledObserver) {
                  this.buttonDisabledObserver.observe(btn, {
                    attributes: true,
                    attributeFilter: ['disabled']
                  });
                }
              });
            }
          }
        });
      });

      if (shouldUpdate) {
        this.debouncedButtonUpdate();
      }
    });

    // Observe main containers for button additions
    const containers = [
      document.body, // Catch all dynamic content
      ...document.querySelectorAll('.product-form, .card-product, .product-item, main')
    ];

    containers.forEach(container => {
      if (container) {
        observer.observe(container, {
          childList: true,
          subtree: true
        });
      }
    });

    // Store observer for cleanup
    this.domObserver = observer;
  }

  /**
   * Handle cart update events
   * FIXED: Correct step calculation based on eligible items only
   */
  async handleCartUpdate() {
    try {
      // Fetch latest cart data
      await this.fetchCartData();

      // Update eligible count
      this.eligibleItemCount = this.getEligibleItemCount();

      // Update BOGO presence flag
      this.hasBogoProducts = this.checkHasBOGOItems();

      // Update buttons with debounce
      this.debouncedButtonUpdate();

      // Update progress bar
      this.updateProgressBar();

      // Persist state
      this.persistState();
    } catch (error) {
      // Error handling cart update - continue
    }
  }

  /**
   * Update progress bar based on eligible item count
   * FIXED: Correct logic for steps
   * Step 1: 0 eligible items (Design 1st Pair)
   * Step 2: 1 eligible item (Select FREE Item)
   * Step 3: 2+ eligible items (Finish Your Order)
   */
  updateProgressBar() {
    // Simplified step calculation
    let step = 1; // Default: No items (Add 2 items for BOGO)

    if (this.eligibleItemCount === 0) {
      step = 1; // No eligible items - start shopping
    } else if (this.eligibleItemCount === 1 && !this.hasBogoProducts) {
      step = 2; // 1 item, no BOGO - select free item
    } else if (this.eligibleItemCount >= 1) {
      step = 3; // Has items and/or BOGO - checkout ready
    }

    // Store the current step
    this.currentStep = step;

    // Update progress bar UI
    if (this.progressManager && this.progressManager.setStep) {
      this.progressManager.setStep(step);
    }
  }

  /**
   * Check if cart has any BOGO items
   */
  checkHasBOGOItems() {
    if (!this.cartData || !this.cartData.items) return false;

    const hasBogo = this.cartData.items.some(item => {
      const properties = item.properties || {};
      const isBogo = properties._bogo_item === 'true';
      return isBogo;
    });

    return hasBogo;
  }

  /**
   * Check and show BOGO modal if conditions are met
   */
  checkAndShowBOGOModal() {
    // Check if modal was shown this session (matches old implementation)
    const modalShown = sessionStorage.getItem('bogoModalShown');

    if (modalShown) {
      return; // Already shown this session
    }

    // Show the modal after a short delay (300ms matches old theme)
    setTimeout(() => {
      const modalElement = document.getElementById('bogo-recommendation-modal');

      if (!modalElement || typeof bootstrap === 'undefined') {
        return;
      }

      try {
        const modalInstance = new bootstrap.Modal(modalElement);
        modalInstance.show();
        // Mark as shown for this session
        sessionStorage.setItem('bogoModalShown', 'true');
      } catch (error) {
        // Error showing modal - silently fail
      }
    }, 300);
  }

  /**
   * Clear BOGO modal session for testing
   * Can be called from console: BOGOUtilsAdapter.getInstance().clearBOGOModal()
   */
  clearBOGOModal() {
    sessionStorage.removeItem('bogoModalShown');

    // Optionally trigger immediate check
    if (this.currentStep === 2) {
      this.checkAndShowBOGOModal();
    }
  }

  /**
   * Check if free item selector should be displayed
   * Shows when customer has exactly 1 eligible item and hasn't dismissed today
   */
  checkAndDisplayFreeItemSelector() {
    // Check if selector is enabled
    const selectors = document.querySelectorAll('.free-item-selector');
    if (selectors.length === 0) return;

    // Check if dismissed today
    if (this.isDismissedToday()) {
      this.hideFreeItemSelector();
      return;
    }

    // Show if customer has exactly 1 eligible item
    if (this.eligibleItemCount === 1) {
      this.showFreeItemSelector();
    } else {
      this.hideFreeItemSelector();
    }
  }

  /**
   * Check if selector was dismissed today (24-hour window)
   */
  isDismissedToday() {
    const dismissedUntil = localStorage.getItem('bogo_selector_dismissed_until');
    if (!dismissedUntil) return false;

    const dismissTime = parseInt(dismissedUntil);
    const now = Date.now();

    // If more than 24 hours have passed, clear the flag
    if (now > dismissTime) {
      localStorage.removeItem('bogo_selector_dismissed_until');
      return false;
    }

    return true;
  }

  /**
   * Backward compatibility wrapper
   */
  checkStep2Eligibility() {
    this.checkAndDisplayFreeItemSelector();
  }

  /**
   * Show free item selector inline in cart
   */
  showFreeItemSelector() {
    // Find all selector instances (drawer and page)
    const selectors = document.querySelectorAll('.free-item-selector');
    selectors.forEach(selector => {
      if (selector) {
        selector.style.display = 'block';
        // Add animation class if desired
        selector.classList.add('fade-in');
      }
    });
  }

  /**
   * Hide free item selector
   */
  hideFreeItemSelector() {
    const selectors = document.querySelectorAll('.free-item-selector');
    selectors.forEach(selector => {
      if (selector) {
        selector.style.display = 'none';
      }
    });
  }

  /**
   * Dismiss free item selector for 24 hours
   */
  dismissFreeItemSelectorForToday() {
    // Hide the selector
    this.hideFreeItemSelector();

    // Set dismissal timestamp (24 hours from now)
    const dismissUntil = Date.now() + (24 * 60 * 60 * 1000);
    localStorage.setItem('bogo_selector_dismissed_until', dismissUntil.toString());
  }

  /**
   * Dismiss free item selector (backward compatibility)
   */
  dismissFreeItemSelector() {
    this.dismissFreeItemSelectorForToday();
  }

  /**
   * Initialize inline add-to-cart functionality
   * FIXED: Scoped to modal only, not entire document
   */
  initializeInlineAddToCart() {
    // Only add listener to the modal itself, not the entire document
    const modal = document.getElementById('bogo-recommendation-modal');

    // Also check for inline selectors (though they're disabled)
    const selectors = document.querySelectorAll('.free-item-selector');

    // Add listener to modal if it exists
    if (modal) {
      modal.addEventListener('click', async (e) => {
        // Check if clicked element itself has the attribute (not using closest)
        if (e.target && e.target.hasAttribute('data-bogo-add-inline')) {
          e.preventDefault();
          await this.handleInlineAddToCart(e.target);
        }
      });
    }

    // Add listeners to inline selectors if they exist (though they're hidden)
    selectors.forEach(selector => {
      selector.addEventListener('click', async (e) => {
        if (e.target && e.target.hasAttribute('data-bogo-add-inline')) {
          e.preventDefault();
          await this.handleInlineAddToCart(e.target);
        }
      });
    });
  }

  /**
   * Handle inline add-to-cart for free item selector
   */
  async handleInlineAddToCart(button) {
    try {
      // Disable button and show loading state
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Loading...';

      // Get product data from button attributes
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;
      const productHandle = button.dataset.productHandle;
      const productUrl = button.dataset.productUrl;

      // Must have URL or handle to redirect
      if (!productUrl && !productHandle) {
        throw new Error('No product URL available');
      }

      // Construct the product page URL with BOGO context
      const targetUrl = productUrl || `/products/${productHandle}`;

      // Add BOGO parameters to preserve context
      const url = new URL(targetUrl, window.location.origin);
      url.searchParams.set('bogo', 'true');
      url.searchParams.set('step', '2');
      url.searchParams.set('from', 'inline');

      // Store BOGO context in session for reliability
      sessionStorage.setItem('bogo_redirect_context', JSON.stringify({
        step: 2,
        timestamp: Date.now(),
        fromInline: true,
        productHandle: productHandle,
        variantId: variantId
      }));

      // Close the cart drawer before redirecting
      const cartOffcanvas = document.getElementById('offcanvas-cart');
      if (cartOffcanvas && typeof bootstrap !== 'undefined') {
        const offcanvasInstance = bootstrap.Offcanvas.getInstance(cartOffcanvas);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Small delay to ensure cart closes smoothly
      setTimeout(() => {
        // Redirect to product page for customization
        window.location.href = url.toString();
      }, 200);

    } catch (error) {
      // Reset button on error
      button.disabled = false;
      button.textContent = originalText;

      // Show error message
      alert('Unable to load product. Please refresh and try again.');
    }
  }

  /**
   * Refresh cart drawer/page
   */
  async refreshCart() {
    try {
      // Create a fake successful response object for refreshCartContents
      const fakeResponse = {
        ok: true,
        url: '/cart/add.js'
      };

      // Use the theme's built-in cart refresh function
      if (window.refreshCartContents) {
        await window.refreshCartContents(fakeResponse);
      } else {
        // Fallback: trigger manual refresh
        document.dispatchEvent(new CustomEvent('updated.ecomify.cart'));
      }

      // Fetch updated cart data for our BOGO logic
      await this.fetchCartData();

      // Update BOGO state
      await this.handleCartUpdate();
    } catch (error) {
      // Error refreshing cart - fallback to manual event
      document.dispatchEvent(new CustomEvent('updated.ecomify.cart'));
    }
  }

  /**
   * Save state to sessionStorage
   */
  persistState() {
    const state = {
      eligibleItemCount: this.eligibleItemCount,
      bogoDiscountTitle: this.bogoDiscountTitle,
      currentStep: this.progressManager?.currentStep || 1,
      timestamp: Date.now()
    };

    sessionStorage.setItem('bogoState', JSON.stringify(state));
  }

  /**
   * Load state from sessionStorage
   */
  loadPersistedState() {
    const savedState = sessionStorage.getItem('bogoState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);

        // Check if state is less than 5 minutes old
        if (state.timestamp && (Date.now() - state.timestamp) < 300000) {
          this.eligibleItemCount = state.eligibleItemCount || 0;
          this.bogoDiscountTitle = state.bogoDiscountTitle || null;

          // Update UI based on loaded state
          this.debouncedButtonUpdate();
        }
      } catch (e) {
        // Failed to load state - continue with fresh state
      }
    }
  }

  /**
   * Cleanup observers and timers
   */
  cleanup() {
    // Clear timers
    if (this.updateDebounceTimer) clearTimeout(this.updateDebounceTimer);
    if (this.cartFetchTimer) clearTimeout(this.cartFetchTimer);

    // Disconnect observers
    this.buttonTextObservers.forEach(observer => observer.disconnect());
    this.buttonTextObservers.clear();

    if (this.buttonDisabledObserver) {
      this.buttonDisabledObserver.disconnect();
      this.buttonDisabledObserver = null;
    }

    if (this.domObserver) {
      this.domObserver.disconnect();
      this.domObserver = null;
    }

    // Clear pending updates
    this.pendingButtonUpdates.clear();
  }

  /**
   * Reinitialize after theme editor changes
   */
  reinit() {
    this.cleanup();
    setTimeout(() => this.init(), 100);
  }

  /**
   * Public method to manually trigger update
   */
  refresh() {
    this.fetchCartData().then(() => this.handleCartUpdate());
  }

  /**
   * Destroy instance (for cleanup)
   */
  destroy() {
    this.cleanup();
    window._bogoUtilsAdapter = null;
  }
}

// Initialize both managers when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    BOGOProgressManager.getInstance();
    const bogoAdapter = BOGOUtilsAdapter.getInstance();
    // Make available globally for debugging
    window.BOGOAdapter = bogoAdapter;
    window.clearBOGOModal = () => bogoAdapter.clearBOGOModal();
  });
} else {
  BOGOProgressManager.getInstance();
  const bogoAdapter = BOGOUtilsAdapter.getInstance();
  // Make available globally for debugging
  window.BOGOAdapter = bogoAdapter;
  window.clearBOGOModal = () => bogoAdapter.clearBOGOModal();
}

/**
 * ModalManager Class
 * Handles Bootstrap 5 modals for product blocks
 * Manages money_back, offer_message, and other modal-based blocks
 */
class ModalManager {
  constructor() {
    this.modals = new Map();
    this.init();
  }

  /**
   * Initialize modal manager
   */
  init() {
    try {
      this.setupEventListeners();
      this.handleEscapeKey();
    } catch (error) {
      // Silent fail in production
    }
  }

  /**
   * Setup global event listeners
   */
  setupEventListeners() {
    // Listen for modal trigger clicks
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-modal-trigger]');
      if (trigger) {
        e.preventDefault();
        const modalId = trigger.dataset.modalTrigger;
        this.openModal(modalId);
      }

      // Handle modal close buttons
      const closeBtn = e.target.closest('[data-modal-close]');
      if (closeBtn) {
        e.preventDefault();
        const modal = closeBtn.closest('.modal');
        if (modal) {
          this.closeModal(modal.id);
        }
      }
    });

    // Handle backdrop clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('modal')) {
        const modalBackdrop = e.target.dataset.modalBackdrop;
        if (modalBackdrop !== 'static') {
          this.closeModal(e.target.id);
        }
      }
    });
  }

  /**
   * Handle ESC key for modal closing
   */
  handleEscapeKey() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        const openModals = document.querySelectorAll('.modal.show');
        openModals.forEach(modal => {
          const modalBackdrop = modal.dataset.modalBackdrop;
          if (modalBackdrop !== 'static') {
            this.closeModal(modal.id);
          }
        });
      }
    });
  }

  /**
   * Open a modal by ID
   * @param {string} modalId - The modal element ID
   */
  openModal(modalId) {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) {
      // Modal element not found
      return;
    }

    // Check if Bootstrap Modal is available
    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
      // Use Bootstrap's modal if available
      let bsModal = this.modals.get(modalId);
      if (!bsModal) {
        bsModal = new bootstrap.Modal(modalElement);
        this.modals.set(modalId, bsModal);
      }
      bsModal.show();
    } else {
      // Fallback to manual modal handling
      this.manualOpenModal(modalElement);
    }
  }

  /**
   * Close a modal by ID
   * @param {string} modalId - The modal element ID
   */
  closeModal(modalId) {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) return;

    // Check if Bootstrap Modal is available
    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
      const bsModal = this.modals.get(modalId);
      if (bsModal) {
        bsModal.hide();
      }
    } else {
      // Fallback to manual modal handling
      this.manualCloseModal(modalElement);
    }
  }

  /**
   * Manual modal open (fallback when Bootstrap JS not available)
   * @param {HTMLElement} modalElement
   */
  manualOpenModal(modalElement) {
    // Add classes and attributes
    modalElement.classList.add('show');
    modalElement.style.display = 'block';
    modalElement.setAttribute('aria-hidden', 'false');

    // Create and add backdrop
    let backdrop = document.querySelector('.modal-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop fade show';
      document.body.appendChild(backdrop);
    }

    // Prevent body scroll
    document.body.classList.add('modal-open');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Manual modal close (fallback when Bootstrap JS not available)
   * @param {HTMLElement} modalElement
   */
  manualCloseModal(modalElement) {
    // Remove classes and attributes
    modalElement.classList.remove('show');
    modalElement.style.display = 'none';
    modalElement.setAttribute('aria-hidden', 'true');

    // Remove backdrop
    const backdrop = document.querySelector('.modal-backdrop');
    if (backdrop) {
      backdrop.remove();
    }

    // Restore body scroll
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
  }

  /**
   * Initialize a specific modal with options
   * @param {string} modalId - The modal element ID
   * @param {Object} options - Bootstrap modal options
   */
  initModal(modalId, options = {}) {
    const modalElement = document.getElementById(modalId);
    if (!modalElement || typeof bootstrap === 'undefined') return;

    const bsModal = new bootstrap.Modal(modalElement, options);
    this.modals.set(modalId, bsModal);
    return bsModal;
  }

  /**
   * Check if a modal is open
   * @param {string} modalId - The modal element ID
   * @returns {boolean}
   */
  isModalOpen(modalId) {
    const modalElement = document.getElementById(modalId);
    return modalElement ? modalElement.classList.contains('show') : false;
  }

  /**
   * Toggle a modal
   * @param {string} modalId - The modal element ID
   */
  toggleModal(modalId) {
    if (this.isModalOpen(modalId)) {
      this.closeModal(modalId);
    } else {
      this.openModal(modalId);
    }
  }

  /**
   * Destroy all modal instances (for cleanup)
   */
  destroy() {
    this.modals.forEach((modal) => {
      if (modal && modal.dispose) {
        modal.dispose();
      }
    });
    this.modals.clear();
  }

  /**
   * Get singleton instance
   * @returns {ModalManager}
   */
  static getInstance() {
    if (!window._modalManager) {
      window._modalManager = new ModalManager();
    }
    return window._modalManager;
  }
}

// Initialize ModalManager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ModalManager.getInstance();
  });
} else {
  ModalManager.getInstance();
}

/**
 * AfterpayManager Class
 * Handles Afterpay payment messaging across product pages
 * Restored for compatibility with product-block-afterpay-message.liquid
 */
class AfterpayManager {
  constructor() {
    this.initialized = false;
    this.observer = null;
    this.updateTimer = null;
    this.supportedCurrencies = ['USD', 'AUD', 'NZD', 'CAD'];
    this.init();
  }

  /**
   * Initialize Afterpay manager
   */
  init() {
    if (this.initialized) return;

    try {
      // Check if we have Afterpay blocks on the page
      this.afterpayElements = document.querySelectorAll('[data-afterpay-message]');
      if (!this.afterpayElements.length) return;

      // Initialize observer and listeners
      this.initPriceObserver();
      this.initEventListeners();
      this.updateInstallments();

      this.initialized = true;

      // Handle theme editor reloads
      document.addEventListener('shopify:section:unload', () => this.destroy());
    } catch (error) {
      // Silent fail in production
    }
  }

  /**
   * Initialize MutationObserver for price changes
   */
  initPriceObserver() {
    // Find price container
    const priceContainer = document.querySelector('.product-block-price');
    if (!priceContainer) return;

    // Create observer
    this.observer = new MutationObserver(() => {
      this.debounceUpdate();
    });

    // Observe price changes
    this.observer.observe(priceContainer, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-price', 'data-price-current']
    });
  }

  /**
   * Initialize event listeners
   */
  initEventListeners() {
    // Listen for variant changes (multiple possible events)
    const events = ['variant:change', 'variantPriceChange', 'variant-change'];
    events.forEach(eventName => {
      document.addEventListener(eventName, () => {
        this.debounceUpdate();
      });
    });

    // Listen for Intelligems price updates
    document.addEventListener('intelligems:price_update', () => {
      this.debounceUpdate();
    });
  }

  /**
   * Debounce price updates to prevent flicker
   */
  debounceUpdate() {
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }
    this.updateTimer = setTimeout(() => {
      this.updateInstallments();
    }, 100);
  }

  /**
   * Get current price from the page
   * @returns {number|null} Price in dollars
   */
  getCurrentPrice() {
    // Try multiple selectors
    const selectors = [
      '[data-price-current]',
      '.price-current',
      '.product-block-price .price:not(.price-compare)',
      '[data-product-price]'
    ];

    for (const selector of selectors) {
      const priceElement = document.querySelector(selector);
      if (priceElement) {
        const priceText = priceElement.textContent || priceElement.innerText;
        return this.parsePrice(priceText);
      }
    }

    return null;
  }

  /**
   * Parse price from text
   * @param {string} priceText - Price text with currency symbol
   * @returns {number|null} Price as number
   */
  parsePrice(priceText) {
    if (!priceText) return null;

    try {
      // Remove currency symbols and non-numeric characters (except . and ,)
      const cleanPrice = priceText.replace(/[^\d.,]/g, '');

      // Handle different formats (1,234.56 or 1.234,56)
      let price;
      if (cleanPrice.includes(',') && cleanPrice.includes('.')) {
        // Determine which is decimal separator
        const lastDot = cleanPrice.lastIndexOf('.');
        const lastComma = cleanPrice.lastIndexOf(',');
        if (lastDot > lastComma) {
          // US format: 1,234.56
          price = cleanPrice.replace(/,/g, '');
        } else {
          // EU format: 1.234,56
          price = cleanPrice.replace(/\./g, '').replace(',', '.');
        }
      } else if (cleanPrice.includes(',')) {
        // Check if comma is decimal separator
        const parts = cleanPrice.split(',');
        if (parts.length === 2 && parts[1].length <= 2) {
          // Likely decimal separator
          price = cleanPrice.replace(',', '.');
        } else {
          // Thousands separator
          price = cleanPrice.replace(/,/g, '');
        }
      } else {
        price = cleanPrice;
      }

      return parseFloat(price);
    } catch (error) {
      // Error parsing price
      return null;
    }
  }

  /**
   * Calculate installment amount
   * @param {number} price - Total price
   * @returns {number} Installment amount
   */
  calculateInstallment(price) {
    if (!price || price <= 0) return 0;
    return Math.round((price / 4) * 100) / 100; // Round to 2 decimal places
  }

  /**
   * Format price as currency
   * @param {number} amount - Amount to format
   * @returns {string} Formatted price
   */
  formatMoney(amount) {
    // Check if Shopify formatMoney is available
    if (window.Shopify && window.Shopify.formatMoney) {
      // Convert to cents for Shopify
      const cents = Math.round(amount * 100);
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }

    // Manual formatting fallback
    const currency = window.Shopify?.currency?.active || 'USD';
    const symbol = this.getCurrencySymbol(currency);
    return `${symbol}${amount.toFixed(2)}`;
  }

  /**
   * Get currency symbol
   * @param {string} currency - Currency code
   * @returns {string} Currency symbol
   */
  getCurrencySymbol(currency) {
    const symbols = {
      'USD': '$',
      'AUD': 'A$',
      'NZD': 'NZ$',
      'CAD': 'C$',
      'EUR': '€',
      'GBP': '£'
    };
    return symbols[currency] || '$';
  }

  /**
   * Check if currency is supported by Afterpay
   * @returns {boolean}
   */
  isCurrencySupported() {
    const currency = window.Shopify?.currency?.active || 'USD';
    return this.supportedCurrencies.includes(currency);
  }

  /**
   * Update all Afterpay message elements
   */
  updateInstallments() {
    // Check currency support
    if (!this.isCurrencySupported()) {
      this.hideAllMessages();
      return;
    }

    // Get current price
    const price = this.getCurrentPrice();
    if (!price) {
      this.hideAllMessages();
      return;
    }

    // Calculate installment
    const installment = this.calculateInstallment(price);
    const formattedInstallment = this.formatMoney(installment);

    // Update all Afterpay elements
    this.afterpayElements.forEach(element => {
      const installmentElement = element.querySelector('[data-afterpay-installment]');
      if (installmentElement) {
        installmentElement.textContent = formattedInstallment;
      }

      // Show element if hidden
      element.style.display = '';
    });
  }

  /**
   * Hide all Afterpay messages
   */
  hideAllMessages() {
    this.afterpayElements.forEach(element => {
      element.style.display = 'none';
    });
  }

  /**
   * Destroy and cleanup
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }

    this.initialized = false;
  }

  /**
   * Get singleton instance
   * @returns {AfterpayManager}
   */
  static getInstance() {
    if (!window._afterpayManager) {
      window._afterpayManager = new AfterpayManager();
    }
    return window._afterpayManager;
  }
}

// Initialize AfterpayManager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    AfterpayManager.getInstance();
  });
} else {
  AfterpayManager.getInstance();
}

/**
 * Pet name inputs - Simplified Implementation
 * Functionality moved to template for better reliability
 * See: snippets/product-block-petname-inputs.liquid
 */

// Pet name inputs functionality is now handled directly in the template
// See: snippets/product-block-petname-inputs.liquid

// Engraving functionality is handled by product-engraving.js
// EngravingManager class removed - was causing duplicate popup issue

/* ==================================
   Sticky ATC - Scroll to Product Form
   ================================== */

/**
 * Scrolls smoothly to the main product form on the page
 * Used by the sticky ATC button to return users to product selection
 */
window.scrollToProductForm = function() {
  // Try to find the main product form using multiple selectors
  const productForm = document.querySelector('.product-info') ||
                      document.querySelector('.product__info') ||
                      document.querySelector('[data-product-form]') ||
                      document.querySelector('.product-form') ||
                      document.querySelector('form[action*="/cart/add"]') ||
                      document.querySelector('#product-info');

  if (productForm) {
    // Account for fixed header height (adjust if needed)
    const headerOffset = 100;
    const elementPosition = productForm.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    // Smooth scroll with fallback for older browsers
    if ('scrollBehavior' in document.documentElement.style) {
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      // Fallback for older browsers - instant scroll
      window.scrollTo(0, offsetPosition);
    }
  } else {
    // If no product form found, scroll to top of page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
};

/**
 * Product Blocks Manager System
 * Unified initialization for all product block features
 */
class ProductBlocksSystem {
  static initializeAll() {
    // Check dependencies
    if (!this.checkDependencies()) {
      return;
    }

    // Initialize managers in order
    try {
      // Modal system first (needed by others)
      if (typeof ModalManager !== 'undefined') {
        ModalManager.getInstance();
      }

      // Then specific block managers
      // AfterpayManager removed - handled by snippet directly
      // PetNameManager removed - handled by template directly
      // EngravingManager removed - handled by product-engraving.js
    } catch (error) {
      // Silent fail in production
    }
  }

  static checkDependencies() {
    // Check for Bootstrap
    if (typeof bootstrap === 'undefined') {
      // Modal manager has fallback, so continue
    }

    return true;
  }

  static reinitialize() {
    // Destroy all managers
    const managers = [
      ModalManager,
      AfterpayManager
    ];

    managers.forEach(Manager => {
      if (typeof Manager !== 'undefined') {
        const instance = Manager.getInstance();
        if (instance && instance.destroy) {
          instance.destroy();
        }
      }
    });

    // Reinitialize
    setTimeout(() => {
      this.initializeAll();
    }, 100);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ProductBlocksSystem.initializeAll();
  });
} else {
  // DOM already loaded
  setTimeout(() => {
    ProductBlocksSystem.initializeAll();
  }, 100);
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', () => {
  ProductBlocksSystem.reinitialize();
});

document.addEventListener('shopify:section:unload', () => {
  // Managers handle their own cleanup
});

/* ==================================
   Gift Card Message Feature - DISABLED
   ==================================

   This feature has been disabled in favor of direct-add upsells.
   Preserved for future use if gift message functionality is needed.

   To re-enable:
   1. Uncomment the GiftCardMessageManager class below
   2. Uncomment the modal HTML in offcanvas-cart.liquid (lines ~483-531)
   3. Add 'gift-message-modal' back to CART_MODAL_IDS array below
   4. Remove onclick="addToCartUpsell(this)" from the warranty card button

   Last working: 2024-11-27
*/

/*
class GiftCardMessageManager {
  constructor() {
    this.modal = null;
    this.bsModal = null;
    this.messageInput = null;
    this.charCounter = null;
    this.variantId = null;
    this.maxChars = 350;
    this.initialized = false;

    this.init();
  }

  static getInstance() {
    if (!window._giftCardMessageManager) {
      window._giftCardMessageManager = new GiftCardMessageManager();
    }
    return window._giftCardMessageManager;
  }

  init() {
    if (this.initialized) return;

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupElements());
    } else {
      this.setupElements();
    }
  }

  setupElements() {
    // Get modal element
    this.modal = document.getElementById('gift-message-modal');
    if (!this.modal) return;

    // Initialize Bootstrap modal
    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
      this.bsModal = new bootstrap.Modal(this.modal, {
        backdrop: 'static',
        keyboard: false
      });
    }

    // Get form elements
    this.messageInput = document.getElementById('gift-message-input');
    this.charCounter = document.getElementById('char-count');

    // Setup event listeners
    this.attachEventListeners();

    // Make gift card clickable
    this.makeGiftCardClickable();

    this.initialized = true;
  }

  makeGiftCardClickable() {
    // Use event delegation for dynamic content
    document.addEventListener('click', this.handleGiftCardClick.bind(this));
  }

  handleGiftCardClick(e) {
    // Check if clicked on warranty card (gift card)
    const giftCard = e.target.closest('.warranty-card');
    if (!giftCard) return;

    // If clicking the add button directly, show modal first
    const isAddButton = e.target.closest('.warranty-add');

    if (isAddButton) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Get variant ID from the add button
    const addButton = giftCard.querySelector('.warranty-add');
    if (!addButton) return;

    this.variantId = addButton.dataset.variantId;
    if (!this.variantId) return;

    // Open message modal
    this.openMessageModal();
  }

  attachEventListeners() {
    // Character counter
    if (this.messageInput) {
      this.messageInput.addEventListener('input', () => {
        this.updateCharCounter();
      });

      // Handle paste to check character limit
      this.messageInput.addEventListener('paste', (e) => {
        setTimeout(() => {
          if (this.messageInput.value.length > this.maxChars) {
            this.messageInput.value = this.messageInput.value.substring(0, this.maxChars);
            this.updateCharCounter();
          }
        }, 10);
      });
    }

    // Add with message button
    const addWithMessageBtn = document.getElementById('add-with-message');
    if (addWithMessageBtn) {
      addWithMessageBtn.addEventListener('click', () => {
        this.addToCartWithMessage();
      });
    }

    // Clear message on modal hide
    if (this.modal) {
      this.modal.addEventListener('hidden.bs.modal', () => {
        this.clearForm();
      });
    }
  }

  openMessageModal() {
    // Clear previous message
    this.clearForm();

    // Ensure modal is properly reset and visible (defensive fix)
    if (this.modal) {
      this.modal.style.display = '';
      this.modal.style.visibility = '';
      this.modal.style.opacity = '';
    }

    // Show modal
    if (this.bsModal) {
      this.bsModal.show();
    }
  }

  clearForm() {
    if (this.messageInput) {
      this.messageInput.value = '';
    }
    this.updateCharCounter();
  }

  updateCharCounter() {
    if (!this.messageInput || !this.charCounter) return;

    const length = this.messageInput.value.length;
    this.charCounter.textContent = length;

    const counterParent = this.charCounter.closest('.character-counter');
    if (!counterParent) return;

    // Update color based on character count
    counterParent.classList.remove('text-warning', 'text-danger');

    if (length > 300) {
      counterParent.classList.add('text-warning');
      counterParent.classList.remove('text-muted');
    } else {
      counterParent.classList.add('text-muted');
    }

    if (length >= this.maxChars) {
      counterParent.classList.add('text-danger');
      counterParent.classList.remove('text-warning', 'text-muted');
    }
  }

  addToCartWithMessage() {
    if (!this.variantId) return;

    // Get button for loading state
    const addWithBtn = document.getElementById('add-with-message');

    // Disable button and show loading
    if (addWithBtn) {
      addWithBtn.disabled = true;
      addWithBtn.textContent = 'Adding...';
    }

    // Prepare cart data
    const cartData = {
      id: this.variantId,
      quantity: 1
    };

    // Add message as line item property if provided
    if (this.messageInput && this.messageInput.value.trim()) {
      cartData.properties = {
        'Gift Message': this.messageInput.value.trim()
      };
    }

    // Add to cart
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(cartData)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Close modal
      if (this.bsModal) {
        this.bsModal.hide();
      }

      // Refresh cart
      if (window.refreshCartContents) {
        window.refreshCartContents(response);
      }

      // Show success feedback
      this.showSuccess();
    })
    .catch(error => {
      console.error('Error adding gift card:', error);
      alert('Failed to add gift card. Please try again.');
    })
    .finally(() => {
      // Reset button
      if (addWithBtn) {
        addWithBtn.disabled = false;
        addWithBtn.textContent = 'Add with Message';
      }
    });
  }

  showSuccess() {
    // Create temporary success message
    const message = 'Gift card added with your personal message!';

    // If cart drawer exists, show it
    const cartDrawer = document.getElementById('offcanvas-cart');
    if (cartDrawer && typeof bootstrap !== 'undefined') {
      const bsOffcanvas = bootstrap.Offcanvas.getOrCreateInstance(cartDrawer);
      if (bsOffcanvas) {
        bsOffcanvas.show();
      }
    }
  }

  destroy() {
    // Cleanup for theme editor
    document.removeEventListener('click', this.handleGiftCardClick);
    if (this.bsModal) {
      this.bsModal.dispose();
    }
    this.initialized = false;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    GiftCardMessageManager.getInstance();
  });
} else {
  GiftCardMessageManager.getInstance();
}

// Re-initialize on theme editor reload
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('#gift-message-modal') ||
      event.target.querySelector('.warranty-card')) {
    const manager = GiftCardMessageManager.getInstance();
    manager.destroy();
    manager.init();
  }
});
*/

/**
 * Modal Enhancement Manager
 * ========================================
 * Handles:
 * 1. Cart-triggered modal backdrop elevation (BOGO, gift-message)
 * 2. iOS scroll lock for all modals (fixes scroll bleed-through)
 *
 * Architecture:
 * - Regular modals use Bootstrap defaults (modal 1055, backdrop 1050)
 * - Cart-triggered modals need elevated backdrop (3004) to appear above cart (3000)
 * - Backdrop elevation happens via JS event, not static CSS
 * - iOS scroll lock uses position:fixed with scroll position preservation
 */
(function initModalEnhancements() {
  // Cart-triggered modals that need elevated backdrop
  // Note: 'gift-message-modal' removed - feature disabled (see GiftCardMessageManager section)
  const CART_MODAL_IDS = ['bogo-recommendation-modal'];

  // Scroll position storage for iOS scroll lock
  let scrollPosition = 0;

  /**
   * iOS Scroll Lock
   * Bootstrap's overflow:hidden doesn't work on iOS Safari
   * Using position:fixed with stored scroll position
   */
  document.addEventListener('show.bs.modal', function(e) {
    // Store current scroll position
    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    // Apply scroll position as negative top (CSS handles position:fixed)
    document.body.style.top = `-${scrollPosition}px`;
  });

  document.addEventListener('hidden.bs.modal', function(e) {
    // Check if any other modals are still open
    const openModals = document.querySelectorAll('.modal.show');
    if (openModals.length === 0) {
      // Reset body styles
      document.body.style.top = '';
      // Restore scroll position
      window.scrollTo(0, scrollPosition);
    }
  });

  /**
   * Cart-Triggered Modal Backdrop Elevation
   * Only elevates backdrop for modals that open from within cart drawer
   */
  CART_MODAL_IDS.forEach(modalId => {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    modal.addEventListener('shown.bs.modal', function() {
      // Backdrop exists now - elevate it above cart (3000)
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.style.zIndex = '3004';
      }
    });
  });
})();
